"use client";

import { FEATURE_FLAGS } from ".constants/featureFlags";
import { Icon, cn, usePortalContext } from "@cloai/uikit";
import { isEmpty } from "lodash";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { CSSProperties, useEffect, useMemo, useRef } from "react";

import { useUndressContext, useUndressSettingsContext } from "@/providers";
import ImgSet from "@/styles/image/start-screen/collection.webp";
import IconKeyboard from "@/styles/image/start-screen/keyboard.svg";
import ImgLatex from "@/styles/image/start-screen/latex.webp";
import ImgNude from "@/styles/image/start-screen/nude.webp";
import ImgPose from "@/styles/image/start-screen/shibari.webp";

import { START_SCREEN_STYLES } from "./styles";
import { useClickStart } from "./useClickStart";

/**
 * Компонент стартового экрана с выбором режима генерации изображений
 *
 * @description
 * Компонент отображает сетку с различными режимами генерации:
 * - Video - генерация видео (может быть отключена через FEATURE_FLAGS)
 * - Nude - режим обнаженных фото с выбором позы как доп.опции
 * - Costume - режим с выбором костюма с выбором позы как доп.опции
 * - Pose - режим с выбором позы с выбором костюма как доп.опции (`initial: Naked`)
 * - Prompt - текстовый режим с вводом промпта с выбором позы как доп.опции
 * - Collection - режим выбора из коллекции - без доп.опций
 *
 * Основные обработчики событий:
 * @callback handleClickNude - Обработчик для режима Nude
 * @callback handleClickCostume - Обработчик для режима Costume
 * @callback handleClickPose - Обработчик для режима Pose
 * @callback handleClickPrompt - Обработчик для режима Prompt
 *
 * @component
 */
export const StartV1 = () => {
  const t = useTranslations("HomePage.startScreen");
  const { reUseFaceObj } = useUndressContext();
  const { costumes, poses, sets, storylines, clips } =
    useUndressSettingsContext();
  const { currentPortal } = usePortalContext();

  /**
   * Маркеры пресетов - выключены, если не выполнены условия
   */
  const disabledSettings = useMemo(() => {
    const isCostumesDisabled = isEmpty(costumes);
    const isPosesDisabled = isEmpty(poses);
    const isCollectionsDisabled = isEmpty(sets) && isEmpty(storylines);
    const isClipsDisabled = !FEATURE_FLAGS.IS_CLIPS_ENABLED || isEmpty(clips);

    return {
      isCostumesDisabled,
      isPosesDisabled,
      isCollectionsDisabled,
      isClipsDisabled,
    };
  }, [clips, costumes, poses, sets, storylines]);

  const {
    handleClickVideo,
    handleClickNude,
    handleClickCostume,
    handleClickPose,
    handleClickPrompt,
    handleClickCollection,
  } = useClickStart();

  const videoRef = useRef<HTMLVideoElement>(null);
  /**
   * Ставим видео на паузу при открытом попапе,
   * т.к. браузер не вывозит блюр над видео с альфа-каналом
   */
  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      if (currentPortal && video.played) {
        video.pause();
      } else if (video.paused) {
        video.play();
      }
    }
  }, [currentPortal]);

  return (
    <>
      {!reUseFaceObj && (
        <h1 className={START_SCREEN_STYLES.title}>
          {t("chooseGenerationMode")}
        </h1>
      )}

      <div className={START_SCREEN_STYLES.grid}>
        {/* CLIP */}
        <div
          className={cn(
            START_SCREEN_STYLES.card.base,
            START_SCREEN_STYLES.card.variant.primary,
            disabledSettings.isClipsDisabled &&
              START_SCREEN_STYLES.card.disabled,
          )}
          onClick={handleClickVideo}
        >
          <span
            className={cn(
              START_SCREEN_STYLES.text.base,
              START_SCREEN_STYLES.text.variant.bottom,
              START_SCREEN_STYLES.text.variant.primary,
            )}
          >
            {t("video")}
          </span>

          <video
            ref={videoRef}
            muted
            playsInline
            autoPlay
            loop
            controls={false}
            poster="/images/start-video-2-poster.webp"
            className={cn(
              "pointer-events-none relative block size-full object-contain object-[40%]",
              "md:object-cover",
            )}
          >
            <source
              src="/video/start-video-1.mp4"
              type='video/mp4; codecs="hvc1"'
            />
            <source src="/video/start-video-1.webm" type="video/webm" />
          </video>

          <span
            className={cn(
              START_SCREEN_STYLES.text.base,
              START_SCREEN_STYLES.text.variant.bottom,
              START_SCREEN_STYLES.text.variant.primary,
              START_SCREEN_STYLES.text.variant.stroke,
            )}
            style={
              {
                WebkitTextStroke: "2px var(--color-primary)",
              } as CSSProperties
            }
          >
            {t("video")}
          </span>
          <div
            className={cn(
              START_SCREEN_STYLES.badge.base,
              !disabledSettings.isClipsDisabled &&
                START_SCREEN_STYLES.badge.animate,
            )}
          >
            {disabledSettings.isClipsDisabled ? (
              t("commingSoon")
            ) : (
              <>
                <div className="relative">
                  <Icon
                    icon="verified"
                    size={5}
                    className="absolute animate-ping opacity-20"
                  />
                  <Icon icon="verified" size={5} className="relative" />
                </div>
                {t("availableNow")}
              </>
            )}
          </div>
        </div>

        {/* NUDE+COSTUME+POSE */}
        <div
          className={cn(
            START_SCREEN_STYLES.card.variant.group,
            START_SCREEN_STYLES.card.variant.secondary,
          )}
        >
          <div
            className={cn(
              START_SCREEN_STYLES.card.base,
              disabledSettings.isCostumesDisabled &&
                START_SCREEN_STYLES.card.disabled,
            )}
            onClick={handleClickNude}
          >
            <Image
              src={ImgNude}
              fill
              sizes="100vw"
              alt=""
              priority
              quality={100}
              className={cn(START_SCREEN_STYLES.image)}
            />

            <div
              className={cn(
                START_SCREEN_STYLES.text.base,
                START_SCREEN_STYLES.text.variant.bottom,
              )}
            >
              <span>{t("nude")}</span>
            </div>
          </div>

          <div
            className={cn(
              START_SCREEN_STYLES.card.base,
              disabledSettings.isCostumesDisabled &&
                START_SCREEN_STYLES.card.disabled,
            )}
            onClick={handleClickCostume}
          >
            <Image
              src={ImgLatex}
              fill
              sizes="100vw"
              alt=""
              priority
              quality={100}
              className={START_SCREEN_STYLES.image}
            />
            <div
              className={cn(
                START_SCREEN_STYLES.text.base,
                START_SCREEN_STYLES.text.variant.bottom,
              )}
            >
              <span>{t("costume")}</span>
            </div>
          </div>

          <div
            className={cn(
              START_SCREEN_STYLES.card.base,
              disabledSettings.isPosesDisabled &&
                START_SCREEN_STYLES.card.disabled,
            )}
            onClick={handleClickPose}
          >
            <Image
              src={ImgPose}
              fill
              sizes="100vw"
              alt=""
              priority
              quality={100}
              className={START_SCREEN_STYLES.image}
            />
            <span
              className={cn(
                START_SCREEN_STYLES.text.base,
                START_SCREEN_STYLES.text.variant.bottom,
              )}
            >
              {t("pose")}
            </span>
          </div>
        </div>

        {/* PROMPT */}
        <div className={START_SCREEN_STYLES.prompt} onClick={handleClickPrompt}>
          <Image
            src={IconKeyboard}
            width={150}
            height={150}
            alt="keyboard icon"
            className="absolute left-0 -rotate-12 opacity-5"
          />
          <span
            className={cn(START_SCREEN_STYLES.text.base, "text-container-6")}
          >
            {t("prompt")}
          </span>
        </div>

        {/* COLLECTION */}
        <div
          className={cn(
            START_SCREEN_STYLES.card.base,
            START_SCREEN_STYLES.card.variant.regular,
            disabledSettings.isCollectionsDisabled &&
              START_SCREEN_STYLES.card.disabled,
          )}
          onClick={handleClickCollection}
        >
          <span
            className={cn(
              START_SCREEN_STYLES.text.base,
              START_SCREEN_STYLES.text.variant.bottom,
            )}
          >
            {t("collection")}
          </span>
          <Image
            src={ImgSet}
            fill
            sizes="100vw"
            alt=""
            priority
            quality={100}
            className={START_SCREEN_STYLES.image}
          />
          <span
            className={cn(
              START_SCREEN_STYLES.text.base,
              START_SCREEN_STYLES.text.variant.bottom,
              START_SCREEN_STYLES.text.variant.stroke,
            )}
            style={
              {
                WebkitTextStroke: "1px var(--color-white)",
              } as CSSProperties
            }
          >
            {t("collection")}
          </span>
          <div
            className={cn(
              START_SCREEN_STYLES.badge.base,
              START_SCREEN_STYLES.badge.animate,
            )}
          >
            <div className="relative">
              <Icon
                icon="cards_star"
                size={5}
                className="absolute animate-ping opacity-20"
              />
              <Icon icon="cards_star" size={5} className="relative" />
            </div>
            {t("newStaffInside")}
          </div>
        </div>
      </div>
    </>
  );
};
